// components/parcours-client/SignatureStep.js
import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SignaturePad from "react-signature-canvas";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useTranslation } from "react-i18next";

const SignatureStep = ({ handleBack, handleNext, formData, setFormData }) => {
  const { t } = useTranslation("parcours-client/SignatureStep");

  const signatureRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [consentChecked, setConsentChecked] = useState(false);
  const [error, setError] = useState("");

  // Restaurer la signature si elle existe
  useEffect(() => {
    if (formData.signature && signatureRef.current) {
      try {
        signatureRef.current.fromDataURL(formData.signature);
      } catch {
        // ignore if invalid
      }
    }
  }, [formData.signature]);

  // Adapter dynamiquement la largeur du canvas
  useEffect(() => {
    const handleResize = () => {
      if (canvasContainerRef.current) {
        setCanvasWidth(canvasContainerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = () => {
    const isEmpty = signatureRef.current?.isEmpty();

    if (!consentChecked) {
      setError(t("errors.needAccept"));
      return;
    }
    if (isEmpty) {
      setError(t("errors.needSign"));
      return;
    }

    setError("");
    const signatureDataUrl = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/png");

    setFormData({ ...formData, signature: signatureDataUrl });
    handleNext();
  };

  const handleClear = () => {
    signatureRef.current?.clear();
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 6 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "900px",
          borderRadius: "20px",
          px: { xs: 3, md: 6 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          backgroundColor: "#F8F8F8",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Typography fontWeight={800} fontSize="1.8rem" mb={3}>
          {t("title")}
        </Typography>

        <Box sx={{ backgroundColor: "#fff", borderRadius: 2, p: 3, mb: 3 }}>
          <Typography fontSize="0.95rem" mb={1}>
            {t("intro.title")}
          </Typography>
          <ul style={{ paddingLeft: "1.2rem", fontSize: "0.9rem", color: "#444", margin: 0 }}>
            <li>{t("intro.items.0")}</li>
            <li>{t("intro.items.1")}</li>
            <li>{t("intro.items.2")}</li>
            <li>{t("intro.items.3")}</li>
          </ul>
        </Box>

        <Typography fontWeight={600} mb={1}>
          {t("emailLabel")}{" "}
          <Box component="span" fontWeight={700}>
            {formData.email}
          </Box>
        </Typography>

        <Box sx={{ my: 3 }}>
          <Typography fontWeight={600} mb={1}>
            {t("signBoxLabel")}
          </Typography>
          <Box
            ref={canvasContainerRef}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              height: 200,
              backgroundColor: "#fff",
              mb: 1,
            }}
          >
            <SignaturePad
              ref={signatureRef}
              canvasProps={{
                width: canvasWidth,
                height: 200,
                style: { width: "100%", height: "100%", borderRadius: "8px" },
              }}
            />
          </Box>
          <Button onClick={handleClear} variant="outlined" sx={{ fontSize: "0.8rem" }}>
            {t("actions.clear")}
          </Button>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              sx={{ color: "#4eb933" }}
            />
          }
          label={
            <Typography sx={{ fontSize: "0.85rem" }}>
              {t("consent")}
            </Typography>
          }
        />

        {error && (
          <Typography sx={{ color: "red", fontSize: "0.85rem", mt: 1 }}>
            {error}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: { xs: "center", sm: "space-between" },
            gap: 2,
            mt: 4,
          }}
        >
          <Button
            onClick={handleBack}
            variant="outlined"
            fullWidth
            startIcon={<ArrowBackIosIcon />}
            sx={{
              borderRadius: "12px",
              fontWeight: 600,
              fontSize: { xs: "0.95rem", md: "1rem" },
              py: 1.2,
              textTransform: "none",
            }}
          >
            {t("actions.prev")}
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
            fullWidth
            sx={{
              backgroundColor: "#4eb933",
              color: "#fff",
              "&:hover": { backgroundColor: "#3e9e2a" },
              fontWeight: 700,
              borderRadius: "12px",
              fontSize: { xs: "0.95rem", md: "1rem" },
              py: 1.2,
              px: 3,
              textTransform: "none",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            {t("actions.submit")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignatureStep;